/**
 * useCaseDetail — consumer hook for one case's detail (038 US1).
 *
 * Mirrors `useCases`' Suspense discipline (a module-level cache OUTSIDE React so
 * the promise survives the suspend-retry and resets when composition swaps the
 * adapter), but keyed per (port, caseId): the outer WeakMap keys on the port
 * instance (GC'd with it, reset on adapter swap); the inner Map keys on the
 * caseId so opening different cases doesn't collide. Route-on-read — the detail
 * re-fetches on reload; no local copy (FR-006/FR-009).
 *
 * Only call inside a Suspense + PortErrorBoundary boundary (`CaseDetailBody` wraps
 * its body exactly as `CasesList` does).
 */

import React, { useContext } from 'react';
import type { LanguageCode } from '../i18n/LanguagePort.js';
import { PortsContext } from '../providers/PortsContext.js';
import type { CaseDetailView, CasesReaderPort } from './CasesReaderPort.js';

interface DetailCacheEntry {
  readonly promise: PromiseLike<CaseDetailView>;
  /**
   * Eviction signal resolved off the promise. Set for a REJECTED promise AND
   * for a fulfilled-but-TRANSIENT view (`not-found`/`unavailable`): both are
   * evicted on the NEXT render (deferred) so a later mount re-fetches
   * (route-on-read), while an `ok` view stays cached for the port lifetime.
   * Deferred — never on the fulfillment microtask — so React's suspend-retry
   * still COMMITS the resolved value before the entry is dropped (see below).
   */
  evict: boolean;
}

const detailPromiseCache = new WeakMap<CasesReaderPort, Map<string, DetailCacheEntry>>();

function isPromiseLike(
  value: CaseDetailView | PromiseLike<CaseDetailView>,
): value is PromiseLike<CaseDetailView> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

export function useCaseDetail(
  caseId: string,
  language: LanguageCode = 'fi',
  // 040 US1 — route-on-read after a successful reply/upload: bumping this token
  // changes the cache key, so the detail is RE-READ live from the back office
  // (never a local append to the thread/attachment list, P7). Defaults to 0 so
  // existing callers (and a first mount) read exactly once.
  reloadToken = 0,
): CaseDetailView {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useCaseDetail: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }
  if (ports.memberCases === undefined) {
    throw new Error(
      'useCaseDetail: memberCases port is not registered. This feature requires the member-cases port to be configured.',
    );
  }

  const port = ports.memberCases;
  let byId = detailPromiseCache.get(port);
  if (byId === undefined) {
    byId = new Map<string, DetailCacheEntry>();
    detailPromiseCache.set(port, byId);
  }

  // Key per (caseId, language, reloadToken) so a language switch re-reads the
  // detail in the new language (039 US2, FR-011), and a post-write reload token
  // bump re-reads the same case+language live (040 US1, route-on-read) — each
  // without colliding with the prior read.
  const key = `${language}:${caseId}:${reloadToken}`;
  let entry = byId.get(key);
  if (entry === undefined) {
    // Route-on-read bumps `reloadToken`, minting a new key each successful
    // reply/upload. A fulfilled `ok` entry is never self-evicted, so prior-token
    // entries for this same (language, caseId) would accumulate as dead retention.
    // Drop them now that a newer read supersedes them — only the current read is
    // kept (per-(language,caseId), one entry).
    const prefix = `${language}:${caseId}:`;
    for (const existingKey of byId.keys()) {
      if (existingKey.startsWith(prefix) && existingKey !== key) {
        byId.delete(existingKey);
      }
    }
    const viewOrPromise = port.getCaseDetail(caseId, language);
    if (!isPromiseLike(viewOrPromise)) {
      // Synchronous adapter (test fixture) — nothing to suspend on.
      return viewOrPromise;
    }
    const newEntry: DetailCacheEntry = { promise: viewOrPromise, evict: false };
    // Flag the entry for eviction on a LATER render — NEVER evict here on the
    // fulfillment microtask. Evicting mid-flight (before React's suspend-retry
    // commits the resolved value) drops the state the boundary is about to
    // render and re-fetches on the retry, looping forever. We only RECORD the
    // outcome; the eviction itself is deferred in the `else if` below, exactly
    // as the rejected path and `useCases` do.
    viewOrPromise.then(
      (view) => {
        if (view.status !== 'ok') {
          newEntry.evict = true;
        }
      },
      () => {
        newEntry.evict = true;
      },
    );
    byId.set(key, newEntry);
    entry = newEntry;
  } else if (entry.evict) {
    // Deferred eviction (the useCases rationale): a transient/rejected result
    // resolved on a PRIOR render is dropped AFTER React commits this one — the
    // retry still reads the resolved value (no loop), and a later mount
    // (navigate-away-and-back) finds an empty cache and re-fetches. One per mount.
    const scopedById = byId;
    queueMicrotask(() => scopedById.delete(key));
  }

  return React.use(entry.promise);
}
